import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../../../store/admin/vehiclesSocketStore";
import {
  useDeviceEventsStore,
  getEventLabel,
  type SocketEvent,
} from "../../../../../../../store/admin/deviceEventsStore";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

// Helper para mostrar el label del evento (si es código 43, muestra event_type)
const getEventDisplayLabel = (event: SocketEvent): string => {
  if (event.event_code === 43 && event.event_type) {
    return event.event_type;
  }
  return getEventLabel(event.event_code);
};

interface EventsHistoryModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
  onCreateEvent: () => void;
}

export default function EventsHistoryModal({
  vehicle,
  isDarkMode,
  onClose,
  onCreateEvent,
}: EventsHistoryModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const [currentPage, setCurrentPage] = useState(1);

  // Obtener eventos del socket para este vehículo
  const eventsByDevice = useDeviceEventsStore((state) => state.eventsByDevice);
  const socketEvents = useMemo<SocketEvent[]>(() => {
    if (!vehicle?.id) return [];
    return eventsByDevice[vehicle.id] || [];
  }, [eventsByDevice, vehicle?.id]);

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const headerBg = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const rowHoverBg = isDarkMode ? "hover:bg-[#252525]" : "hover:bg-gray-50";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const ITEMS_PER_PAGE = 10;

  // Paginación local
  const totalItems = socketEvents.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return socketEvents.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [socketEvents, currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Formatear timestamp a fecha/hora local
  const formatEventTime = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatEventDate = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden border ${borderColor} shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-calendar-event-line text-black text-lg"></i>
            </div>
            <div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("eventsHistoryModal.title")}
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                {vehicle.vehicle.name} • {t("eventsHistoryModal.eventsCount", { count: totalItems })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            } rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {socketEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i
                className={`ri-calendar-event-line text-4xl ${textSecondary} mb-3`}
              ></i>
              <p className={textSecondary}>{t("eventsHistoryModal.noSessionEvents")}</p>
              <p className={`text-xs ${textSecondary} mt-1`}>
                {t("eventsHistoryModal.eventsWillAppear")}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className={`${headerBg} sticky top-0`}>
                <tr>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("eventsHistoryModal.event")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("eventsHistoryModal.dateTime")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("eventsHistoryModal.code")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("eventsHistoryModal.coordinates")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((event, index) => (
                  <tr
                    key={event.id}
                    className={`${rowHoverBg} transition-colors ${
                      index % 2 === 1
                        ? isDarkMode
                          ? "bg-[#1f1f1f]"
                          : "bg-gray-50/50"
                        : ""
                    }`}
                  >
                    <td
                      className={`py-4 px-4 ${textColor} text-sm font-medium`}
                    >
                      {getEventDisplayLabel(event)}
                    </td>
                    <td className={`py-4 px-4 ${textColor} text-sm`}>
                      <div>
                        <span>{formatEventDate(event.timeStamp)}</span>
                        <br />
                        <span className={textSecondary}>
                          {formatEventTime(event.timeStamp)}
                        </span>
                      </div>
                    </td>
                    <td className={`py-4 px-4 ${textSecondary} text-sm`}>
                      {event.event_code ? `#${event.event_code}` : "-"}
                    </td>
                    <td className={`py-4 px-4 ${textSecondary} text-sm`}>
                      {event.lat && event.lon
                        ? `${event.lat.toFixed(5)}, ${event.lon.toFixed(5)}`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer con paginación y botón crear */}
        <div
          className={`p-4 border-t ${borderColor} flex items-center justify-between`}
        >
          {/* Botón Crear evento */}
          <button
            onClick={onCreateEvent}
            className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors"
          >
            {t("eventsHistoryModal.createEvent")}
          </button>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className={`p-2 rounded-lg border ${borderColor} ${
                  currentPage <= 1
                    ? "opacity-50 cursor-not-allowed"
                    : isDarkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-100"
                } transition-colors`}
              >
                <i className={`ri-arrow-left-s-line ${textSecondary}`}></i>
              </button>

              <span className={`text-sm ${textSecondary}`}>
                {t("eventsHistoryModal.page")} {currentPage} {t("eventsHistoryModal.of")} {totalPages}
              </span>

              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className={`p-2 rounded-lg border ${borderColor} ${
                  currentPage >= totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : isDarkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-100"
                } transition-colors`}
              >
                <i className={`ri-arrow-right-s-line ${textSecondary}`}></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
